import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo } from "react";

import {
  setChosenCreditCard as setChosenCreditCardInStore,
  setChosenShippingAddress as setChosenShippingAddressInStore,
  setSetupComplete,
  setShoppingCartCopy,
} from "@/store/checkoutSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useLazyGetShoppingCartQuery } from "@/store/shoppingCartApi";
import {
  AccountSummaryState,
  useGetAccountSummaryQuery,
  useGetAllShippingAddressesQuery,
  useGetExistingCreditCardsQuery,
  useGetMyLicensesQuery,
  useGetUserQuery,
} from "@/store/userApi";

export default function useCheckout({ performInitialSetup }: UseCheckoutProps = { performInitialSetup: false }) {
  const router = useRouter();
  const pathname = usePathname();
  const dispatch = useAppDispatch();
  const { data: user, isLoading: userIsLoading } = useGetUserQuery();
  const { data: account, isLoading: accountIsLoading } = useGetAccountSummaryQuery();
  const { data: creditCards = [], isLoading: creditCardsIsLoading } = useGetExistingCreditCardsQuery();
  const { data: myLicenses = [], isLoading: myLicensesIsLoading } = useGetMyLicensesQuery();
  const { data: addresses, isLoading: addressesIsLoading } = useGetAllShippingAddressesQuery();
  const { setupComplete, chosenCreditCard, chosenShippingAddress, checkoutCart, orderStatusError } = useAppSelector(
    (state) => state.checkoutSlice,
  );
  const [getShoppingCart, { isFetching: shoppingCartIsFetching }] = useLazyGetShoppingCartQuery();

  const licenseRequired = useMemo(() => {
    if (!shoppingCartIsFetching && checkoutCart) {
      const isLicenseRequired = checkoutCart.vendorOrders.some((vendorOrder) =>
        vendorOrder.products.some((product) => product.licenseRequired === true),
      );
      return isLicenseRequired;
    }
    return undefined; // Placeholder value when shopping cart is still loading
  }, [shoppingCartIsFetching, checkoutCart]);

  // Licenses only decide the next step for a license-required cart, so only that cart waits for them
  const checkoutIsLoading = useMemo(() => {
    return (
      !checkoutCart ||
      userIsLoading ||
      accountIsLoading ||
      creditCardsIsLoading ||
      addressesIsLoading ||
      (licenseRequired === true && myLicensesIsLoading) ||
      shoppingCartIsFetching
    );
  }, [
    checkoutCart,
    userIsLoading,
    accountIsLoading,
    creditCardsIsLoading,
    addressesIsLoading,
    licenseRequired,
    myLicensesIsLoading,
    shoppingCartIsFetching,
  ]);

  const getSelectedShippingAddress = useCallback(() => {
    return addresses?.find((addr) => addr.Id === chosenShippingAddress);
  }, [chosenShippingAddress, addresses]);

  const getSelectedCreditCard = useCallback(() => {
    return creditCards.find((cc) => cc.CardId === chosenCreditCard);
  }, [chosenCreditCard, creditCards]);

  const currentPage = useMemo(() => {
    if (pathname) {
      return pathname.split("/").pop();
    }
  }, [pathname]);

  /**
   * Provides a hash of the cart to determine if the cart has changed.
   */
  const cartHash = useMemo(() => {
    if (checkoutCart) {
      return checkoutCart.vendorOrders
        .flatMap((vo) =>
          vo.products.flatMap((p) => {
            return `${p.vendorProductId}-${p.quantity}-${p.unitPrice}`;
          }),
        )
        .join("|");
    } else {
      return "";
    }
  }, [checkoutCart]);

  type UpdateSessionStorageProps = {
    chosenCreditCardId?: string | null;
    chosenShippingAddressId?: number | null;
    cartHash?: string;
  };
  const updateSessionStorage = useCallback(
    ({ chosenCreditCardId, chosenShippingAddressId, cartHash: incomingCartHash }: UpdateSessionStorageProps) => {
      try {
        const savedCartInfoFromStorage = window.sessionStorage.getItem("n32SavedCartInfo");
        const parsedSavedCartInfo = savedCartInfoFromStorage ? JSON.parse(savedCartInfoFromStorage) : {};
        const savedCartInfo: SavedCartInfo = {
          chosenCreditCard: chosenCreditCardId ?? parsedSavedCartInfo.chosenCreditCard ?? null,
          chosenShippingAddress: chosenShippingAddressId ?? parsedSavedCartInfo.chosenShippingAddress ?? null,
          cartHash: incomingCartHash ?? parsedSavedCartInfo.cartHash ?? "",
          userId: user?.UserId ?? null,
        };
        window.sessionStorage.setItem("n32SavedCartInfo", JSON.stringify(savedCartInfo));
        console.log("Checkout | Saving chosen credit card and shipping address to session storage:", savedCartInfo);
      } catch (error) {
        console.error(error);
      }
    },
    [user?.UserId],
  );

  useEffect(() => {
    if (!checkoutCart && shoppingCartIsFetching === false) {
      getShoppingCart()
        .unwrap()
        .then((res) => {
          dispatch(setShoppingCartCopy(res));
        });
    }
  }, [dispatch, shoppingCartIsFetching, checkoutCart, getShoppingCart]);

  const setPrimaryCardAsChosenCard = useCallback(() => {
    if (creditCards && creditCards.length > 0) {
      const primaryCard = creditCards.find((cc) => cc.IsPrimary === true);
      if (primaryCard) {
        dispatch(setChosenCreditCardInStore(primaryCard?.CardId));
        updateSessionStorage({ chosenCreditCardId: primaryCard.CardId });
      }
    }
  }, [creditCards, dispatch, updateSessionStorage]);

  const setPrimaryAddressAsChosenShippingAddress = useCallback(() => {
    if (addresses && addresses.length > 0) {
      const primaryShippingAddress = addresses.find((addr) => addr.IsPrimary === true && addr.Type === "S");
      if (primaryShippingAddress) {
        dispatch(setChosenShippingAddressInStore(primaryShippingAddress.Id));
        updateSessionStorage({ chosenShippingAddressId: primaryShippingAddress.Id });
      }
    }
  }, [addresses, updateSessionStorage, dispatch]);

  const setChosenShippingAddress = useCallback(
    (id: Account.Addresses.Address["Id"] | null) => {
      dispatch(setChosenShippingAddressInStore(id));
      updateSessionStorage({ chosenShippingAddressId: id });
    },
    [updateSessionStorage, dispatch],
  );

  const setChosenCreditCard = useCallback(
    (id: Account.Billing.CreditCard["CardId"] | null) => {
      dispatch(setChosenCreditCardInStore(id));
      updateSessionStorage({ chosenCreditCardId: id });
    },
    [dispatch, updateSessionStorage],
  );

  const setupCheckoutFromStore = useCallback(() => {
    setPrimaryCardAsChosenCard();
    setPrimaryAddressAsChosenShippingAddress();
    updateSessionStorage({ cartHash });
    dispatch(setSetupComplete(true));
  }, [cartHash, dispatch, setPrimaryAddressAsChosenShippingAddress, setPrimaryCardAsChosenCard, updateSessionStorage]);

  useEffect(() => {
    try {
      // Wait for the addresses and credit-cards queries before running setup: the primary
      // seeding helpers silently no-op on empty/undefined lists, and setup only runs once
      // (setupComplete) — so if account + cart won the race against those queries, the user's
      // primary address/card never got pre-selected and checkout landed on "new address/card".
      if (
        performInitialSetup &&
        cartHash !== "" &&
        setupComplete === false &&
        account &&
        addressesIsLoading === false &&
        creditCardsIsLoading === false
      ) {
        const savedCartInfoRaw = window.sessionStorage.getItem("n32SavedCartInfo");
        if (savedCartInfoRaw) {
          const savedCartInfo: SavedCartInfo = JSON.parse(savedCartInfoRaw);
          if (savedCartInfo) {
            if (savedCartInfo.cartHash === cartHash && savedCartInfo.userId === user?.UserId) {
              if (savedCartInfo.chosenCreditCard) {
                const potentiallyChosenCard = creditCards.find((cc) => cc.CardId === savedCartInfo.chosenCreditCard);
                if (potentiallyChosenCard) {
                  dispatch(setChosenCreditCardInStore(potentiallyChosenCard?.CardId));
                } else {
                  setPrimaryCardAsChosenCard();
                }
              } else {
                setPrimaryCardAsChosenCard();
              }
              if (savedCartInfo.chosenShippingAddress) {
                const potentiallyChosenAddress = addresses?.find(
                  (addr) => addr.Id === savedCartInfo.chosenShippingAddress,
                );
                if (potentiallyChosenAddress) {
                  dispatch(setChosenShippingAddressInStore(potentiallyChosenAddress.Id));
                } else {
                  setPrimaryAddressAsChosenShippingAddress();
                }
              } else {
                setPrimaryAddressAsChosenShippingAddress();
              }
              dispatch(setSetupComplete(true));
            } else {
              setupCheckoutFromStore();
            }
          } else {
            setupCheckoutFromStore();
          }
        } else {
          setupCheckoutFromStore();
        }
      }
    } catch (error) {
      console.error(error);
      // The saved choices couldn't be read, so fall back to the primary ones and still finish setup
      setupCheckoutFromStore();
    }
  }, [
    account,
    addresses,
    addressesIsLoading,
    cartHash,
    creditCardsIsLoading,
    dispatch,
    performInitialSetup,
    setPrimaryAddressAsChosenShippingAddress,
    setPrimaryCardAsChosenCard,
    setupCheckoutFromStore,
    setupComplete,
    updateSessionStorage,
    user?.UserId,
    creditCards,
  ]);

  const { licenses, hasExpiredLicenses } = useMemo(() => {
    const userLicenses = user?.IsGuestUser === false ? myLicenses : [];
    return {
      licenses: userLicenses,
      hasExpiredLicenses: userLicenses.some((l) => {
        if (l.ExpirationDate) {
          return new Date(l.ExpirationDate ?? "") < new Date();
        } else {
          return false;
        }
      }),
    };
  }, [user, myLicenses]);

  const licenseStepCompleted = useMemo(() => {
    if (licenseRequired && user?.IsGuestUser === false) {
      return myLicenses.length > 0 && !hasExpiredLicenses;
    } else {
      return true;
    }
  }, [licenseRequired, user, myLicenses, hasExpiredLicenses]);

  const shippingStepCompleted = useMemo(() => {
    return !!chosenShippingAddress;
  }, [chosenShippingAddress]);

  const savedCreditCards = useMemo(() => {
    return creditCards;
  }, [creditCards]);

  const billingStepCompleted = useMemo(() => {
    return !!chosenCreditCard;
  }, [chosenCreditCard]);

  const determineNextStep = useCallback(
    // The rename of the props is to ensure that contamination from the state variables does not occur
    ({
      account: accountState,
      billingStepCompleted: thisBillingStepCompleted,
      checkoutIsLoading: thisCheckoutIsLoading,
      licenseRequired: thisLicenseRequired,
      licenseStepCompleted: thisLicenseStepCompleted,
      shippingStepCompleted: thisShippingStepCompleted,
    }: DetermineNextStepsProps): NextStep => {
      if (thisCheckoutIsLoading) {
        return "loading";
      } else if (!accountState) {
        return "login";
      } else if (thisLicenseRequired && !thisLicenseStepCompleted) {
        return "license";
      } else if (!thisShippingStepCompleted) {
        return "shipping";
      } else if (!thisBillingStepCompleted) {
        return "billing";
      } else {
        return "review";
      }
    },
    [],
  );

  const nextStep = useMemo((): NextStep => {
    return determineNextStep({
      account,
      billingStepCompleted,
      checkoutIsLoading,
      licenseRequired,
      licenseStepCompleted,
      shippingStepCompleted,
    });
  }, [
    account,
    billingStepCompleted,
    checkoutIsLoading,
    determineNextStep,
    licenseRequired,
    licenseStepCompleted,
    shippingStepCompleted,
  ]);

  const previousStep = useMemo((): "license" | "shipping" | "billing" | null => {
    const page = currentPage;
    switch (page) {
      case "license":
        return null;
      case "shipping":
        if (licenseRequired) {
          return "license";
        } else {
          return null;
        }
      case "billing":
        return "shipping";
      case "review":
        return "billing";
      default:
        return null;
    }
  }, [currentPage, licenseRequired]);

  const goToNextStep = useCallback(
    ({
      account: incomingAccountState,
      billingStepCompleted: incomingBillingStepCompleted,
      checkoutIsLoading: incomingCheckoutIsLoading,
      licenseRequired: incomingLicenseRequired,
      licenseStepCompleted: incomingLicenseStepCompleted,
      shippingStepCompleted: incomingShippingStepCompleted,
      replace = false,
    }: GoToNextStepProps = {}) => {
      const accountState = incomingAccountState ? incomingAccountState : account;
      const thisBillingStepCompleted = incomingBillingStepCompleted
        ? incomingBillingStepCompleted
        : billingStepCompleted;
      const thisCheckoutIsLoading = incomingCheckoutIsLoading ? incomingCheckoutIsLoading : checkoutIsLoading;
      const thisLicenseRequired = incomingLicenseRequired ? incomingLicenseRequired : licenseRequired;
      const thisLicenseStepCompleted = incomingLicenseStepCompleted
        ? incomingLicenseStepCompleted
        : licenseStepCompleted;
      const thisShippingStepCompleted = incomingShippingStepCompleted
        ? incomingShippingStepCompleted
        : shippingStepCompleted;
      const thisNextStep = determineNextStep({
        account: accountState,
        billingStepCompleted: thisBillingStepCompleted,
        checkoutIsLoading: thisCheckoutIsLoading,
        licenseRequired: thisLicenseRequired,
        licenseStepCompleted: thisLicenseStepCompleted,
        shippingStepCompleted: thisShippingStepCompleted,
      });
      // A step the shopper chose to advance to is pushed, so the browser's back button returns
      // them to the step before it. A step they were sent to is replaced to prevent the browser's
      // back button from immediately redirecting them back to the page they came from.
      const navigate = (href: string) => (replace ? router?.replace(href) : router?.push(href));
      switch (thisNextStep) {
        case "license":
          navigate("/checkout/license");
          break;
        case "shipping":
          navigate("/checkout/shipping");
          break;
        case "billing":
          navigate("/checkout/billing");
          break;
        case "review":
          navigate("/checkout/review");
          break;
        case "login":
          router?.replace(`/login?origin=${encodeURI(pathname as string)}`);
          break;
        case "backToShoppingCart":
          router?.replace("/shopping-cart");
          break;
        default:
          console.error("nextStep is loading. Cannot go to next step.");
          break;
      }
    },
    [
      account,
      billingStepCompleted,
      checkoutIsLoading,
      determineNextStep,
      licenseRequired,
      licenseStepCompleted,
      pathname,
      router,
      shippingStepCompleted,
    ],
  );

  return {
    checkoutIsLoading,
    checkoutCart,
    setupComplete,
    addresses,
    account,
    user,
    chosenShippingAddress,
    orderStatusError,
    chosenCreditCard,
    getSelectedShippingAddress,
    getSelectedCreditCard,
    licenses,
    licenseRequired,
    hasExpiredLicenses,
    licenseStepCompleted,
    shippingStepCompleted,
    savedCreditCards,
    billingStepCompleted,
    readyForReview: licenseStepCompleted && shippingStepCompleted && billingStepCompleted,
    nextStep,
    previousStep,
    goToNextStep,
    setChosenShippingAddress,
    setChosenCreditCard,
  };
}

export type NextStep = "license" | "shipping" | "billing" | "review" | "loading" | "backToShoppingCart" | "login";

type DetermineNextStepsProps = {
  account: AccountSummaryState | undefined | null;
  billingStepCompleted: boolean;
  checkoutIsLoading: boolean;
  licenseRequired: boolean | undefined;
  licenseStepCompleted: boolean;
  shippingStepCompleted: boolean;
};

type GoToNextStepProps = Partial<DetermineNextStepsProps> & {
  /**
   * Replace the current history entry instead of pushing a new one. Pass this when the shopper
   * didn't choose to move so the browser's back button doesn't return them to a URL that
   * only redirects them.
   */
  replace?: boolean;
};

type SavedCartInfo = {
  chosenCreditCard: string | null;
  chosenShippingAddress: number | null;
  cartHash: string;
  userId: number | null;
};

type UseCheckoutProps = {
  performInitialSetup?: boolean;
};
